import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import Highcharts from "@/config/highcharts-config";
import HighchartsReact from "highcharts-react-official";
import { BarChartConfig } from "@/store/slices/chart-config-slice";
import { QuestionResponsesSearchResponse } from "@/models/Filter";
import {
  processStackedBarChartData,
  resolveStackedBarRender,
  remapLiveStackedData,
  resolveStackedSegmentColors,
  type StackedBarTransformResult,
} from "@/utils/stacked-bar-utils";
import EmptyChartState from "@components/common/EmptyChartState";
import StackedBarFooter from "./StackedBarFooter";
import { useStackedBarFooter } from "@hooks/use-stacked-bar-footer";

/** Significance arrow markup, matching the Bar/Grouped Bar convention. */
function getSignificanceArrow(sig?: number): string {
  if (sig === 1) return '<span style="color: #10B981;">▲</span>';
  if (sig === -1) return '<span style="color: #EF4444;">▼</span>';
  return "";
}

interface StackedBarChartProps {
  config?: BarChartConfig;
  /** Dataset the widget belongs to — used to resolve a pre-configured base's live name. */
  datasetId?: string | null;
  filteredData?: QuestionResponsesSearchResponse | null;
  weightsApplied?: boolean;
  hasCustomTitle?: boolean | null;
  dashboardDateOverride?: { startDate: string | null; endDate: string | null };
  viewerState?: {
    rollingAverage: number | null;
    timeUnit: string | null;
    timePeriod: string | null;
  };
  dataAvailabilityDate?: string | null;
}

/**
 * Stacked bar (100%-stacked column/bar) chart. Every visible Stack (Grid
 * Variable) renders as one bar whose contiguous Segment (Grid Value) sections
 * render on a fixed 0–100% axis. Built from single grid questions where every
 * item carries a full descriptiveMetrics[] array — the same response shape as
 * Grouped Bar, transposed with fixed roles (no Group By).
 */
const StackedBarChart: React.FC<StackedBarChartProps> = ({
  config,
  datasetId,
  filteredData,
  weightsApplied = true,
  hasCustomTitle,
  dashboardDateOverride,
  viewerState,
  dataAvailabilityDate,
}) => {
  const { stackLabels, suppressedStackCount, segments, hadAnyStackData } =
    useMemo(() => {
      // Prefer freshly fetched data (respects active dashboard filters); fall
      // back to the id-carrying structure persisted in the saved config.
      const base: StackedBarTransformResult =
        filteredData?.data && Array.isArray(filteredData.data)
          ? remapLiveStackedData(
              processStackedBarChartData(filteredData.data as any[]),
              config?.stackedBar?.chartData,
            )
          : (config?.stackedBar?.chartData ?? { categories: [], series: [] });

      return {
        ...resolveStackedBarRender(base, config?.stackedBar),
        hadAnyStackData: base.categories.length > 0,
      };
    }, [filteredData, config?.stackedBar]);

  const segmentColorById = useMemo(
    () => resolveStackedSegmentColors(config?.stackedBar),
    [config?.stackedBar],
  );

  const options = useMemo<Highcharts.Options>(() => {
    const chartType: "bar" | "column" =
      config?.orientation === "horizontal" ? "bar" : "column";
    const decimals = config?.percentageFormatting ?? 0;
    const fontSize = "12px";
    const showValues = config?.valuePlacement === "inside";
    const statTestingEnabled = config?.statisticalTesting?.enabled ?? false;

    const dataLabels = {
      enabled: !!showValues,
      inside: true,
      allowOverlap: false,
      // HTML labels (for the colored significance arrow), matching Bar/
      // Grouped Bar. The tooltip's `outside: true` keeps it layered above.
      useHTML: true,
      formatter: function (this: any) {
        const base = `${this.y?.toFixed(decimals)}%`;
        if (!statTestingEnabled) return base;
        const arrow = getSignificanceArrow(this.point?.custom?.statSignificance);
        if (!arrow) return base;
        return `<div style="text-align:center;line-height:1.2;">${arrow}<br/>${base}</div>`;
      },
      style: { fontSize, fontWeight: "normal", textOutline: "none" },
    } as Highcharts.PlotColumnOptions["dataLabels"];

    return {
      chart: {
        type: chartType,
        backgroundColor: "transparent",
        height: "auto",
      },
      title: { text: undefined },
      xAxis: {
        categories: stackLabels,
        crosshair: true,
        labels: { style: { fontSize } },
      },
      yAxis: {
        reversedStacks: false,
        // Fixed 0-100% axis — calculated percentages are never renormalized.
        min: 0,
        max: 100,
        title: { text: "Percentage (%)" },
        labels: { format: "{value:.0f}%", style: { fontSize } },
      },
      tooltip: {
        // One segment's facts per tooltip (Stack, Segment, percentage) —
        // unlike Grouped Bar's shared per-category tooltip. The significance
        // marker and any "comparison unavailable" note always show here, even
        // when the on-segment data label omits the marker for space.
        shared: false,
        outside: true,
        style: { zIndex: 9999 },
        formatter: function (this: any) {
          const base = `<b>${this.key}</b><br/>${this.series.name}: ${this.y?.toFixed(decimals)}%`;
          if (!statTestingEnabled) return base;
          const sig = this.point?.custom?.statSignificance;
          if (sig === 1 || sig === -1) {
            const direction = sig === 1 ? "increase" : "decrease";
            return `${base}<br/>Significant ${direction} vs. comparison period`;
          }
          if (sig === 0) {
            return `${base}<br/>No significant difference vs. comparison period`;
          }
          return `${base}<br/>Comparison unavailable`;
        },
      },
      plotOptions: {
        series: {
          events: {
            // Legend entries are non-toggleable — hover/keyboard focus can
            // emphasize a Segment Series without changing the distribution.
            legendItemClick: () => false,
          },
        },
        column: {
          stacking: "normal",
          pointPadding: 0.1,
          groupPadding: 0.2,
          borderWidth: 0,
          dataLabels,
        },
        bar: {
          stacking: "normal",
          pointPadding: 0.1,
          groupPadding: 0.2,
          borderWidth: 0,
          dataLabels,
        },
      },
      legend: {
        enabled: true,
        // Default (non-reversed) legend order already matches series (array)
        // order, which is the saved Segments order. The chart wasn't picking
        // up reversedStacks/legend option changes via incremental updates —
        // see chartKey below, which forces a clean remount instead.
        itemStyle: { fontSize },
      },
      credits: { enabled: false },
      exporting: { enabled: false },
      series: segments.map((s) => ({
        type: chartType,
        name: s.label,
        data: s.data.map((cell) => ({
          y: cell.value,
          custom: { statSignificance: cell.statSignificance },
        })),
        color: segmentColorById.get(s.id),
      })) as Highcharts.SeriesOptionsType[],
    };
  }, [stackLabels, segments, config, segmentColorById]);
  const chartKey = useMemo(
    () =>
      JSON.stringify({
        orientation: config?.orientation,
        segmentIds: segments.map((s) => s.id),
        stackCount: stackLabels.length,
      }),
    [config?.orientation, segments, stackLabels.length],
  );

  const footerData = useStackedBarFooter(config, filteredData, {
    weightsApplied,
    hasCustomTitle,
    dashboardDateOverride,
    viewerState,
    dataAvailabilityDate,
    datasetId,
  });

  if (stackLabels.length === 0 || segments.length === 0) {
    // Two distinct states: the question genuinely has no response data at
    // all, vs. every Stack existed but was dropped for insufficient
    // reportable data (suppression / zero denominator).
    if (hadAnyStackData && suppressedStackCount > 0) {
      return (
        <EmptyChartState message="None of this question's Stacks currently have sufficient reportable data to display." />
      );
    }
    return <EmptyChartState />;
  }

  return (
    <div className="w-full h-full min-h-[100px] bg-white relative flex flex-col">
      <div className="flex-1 min-h-0">
        <HighchartsReact
          key={chartKey}
          highcharts={Highcharts}
          options={options}
          containerProps={{ style: { width: "100%", height: "100%" } }}
        />
      </div>
      {suppressedStackCount > 0 && (
        <Box sx={{ px: 2.5, pt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {suppressedStackCount}{" "}
            {suppressedStackCount === 1 ? "stack has" : "stacks have"}{" "}
            insufficient reportable data and{" "}
            {suppressedStackCount === 1 ? "is" : "are"} not shown.
          </Typography>
        </Box>
      )}
      <StackedBarFooter data={footerData} />
    </div>
  );
};

export default StackedBarChart;
